/**
 * components/UI.jsx
 * Shared design primitives — used across all modules
 * to keep the brand consistent.
 */

/* ── HERO HEADER ────────────────────────────────────────────── */
export function HeroHeader({ title, subtitle, badge, accent = 'orange' }) {
  const accentMap = {
    orange: 'linear-gradient(135deg, rgba(255,112,9,0.10) 0%, rgba(255,6,99,0.08) 100%)',
    gold:   'linear-gradient(135deg, rgba(201,168,76,0.10) 0%, rgba(201,168,76,0.04) 100%)',
    teal:   'linear-gradient(135deg, rgba(11,122,117,0.10) 0%, rgba(45,212,191,0.05) 100%)',
  };
  const borderMap = {
    orange: 'rgba(255,92,26,0.2)',
    gold:   'rgba(201,168,76,0.25)',
    teal:   'rgba(11,122,117,0.3)',
  };
  return (
    <div className="animate-in" style={{
      position:'relative',
      background: accentMap[accent],
      border: `1px solid ${borderMap[accent]}`,
      borderRadius: 14,
      padding: '24px 28px',
      overflow: 'hidden',
      flexShrink: 0,
    }}>
      <div style={{position:'absolute', top:-40, right:-40, width:200, height:200, borderRadius:'50%', background:'radial-gradient(circle, rgba(255,112,9,0.12) 0%, transparent 70%)', pointerEvents:'none'}}/>
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', position:'relative', zIndex:1}}>
        <div>
          <div style={{fontSize:24, fontWeight:700, letterSpacing:'-0.02em', color:'#fff', marginBottom:6}}>{title}</div>
          {subtitle && <div style={{fontSize:12, color:'#bbb', fontFamily:'var(--mono)', letterSpacing:'0.03em'}}>{subtitle}</div>}
        </div>
        {badge && <span className="badge or" style={{padding:'4px 10px', fontSize:11}}>{badge}</span>}
      </div>
    </div>
  );
}

/* ── STAT ROW ────────────────────────────────────────────────── */
export function StatRow({ items }) {
  return (
    <div className="stats-grid">
      {items.map((s, i) => (
        <div key={i} className={`stat ${s.cls || 'or'} animate-in`} style={{animationDelay:`${i*0.05}s`, transition:'transform 0.15s ease'}}
             onMouseEnter={e=>e.currentTarget.style.transform='translateY(-2px)'}
             onMouseLeave={e=>e.currentTarget.style.transform='translateY(0)'}>
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
            <div className="stat-val">{s.val}</div>
            {s.icon && <div style={{fontSize:18, opacity:0.5}}>{s.icon}</div>}
          </div>
          <div className="stat-lbl" style={{color:'#aaa'}}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

/* ── SECTION CARD ─────────────────────────────────────────────── */
export function Section({ title, action, children, animationDelay = '0.2s' }) {
  return (
    <div className="card animate-in" style={{animationDelay, flexShrink: 0, overflow: 'visible'}}>
      <div className="panel-hdr">
        <span className="panel-title" style={{color:'#fff'}}>{title}</span>
        {action && <span style={{fontFamily:'var(--mono)', fontSize:11, color:'var(--or)', cursor:'pointer'}}>{action}</span>}
      </div>
      <div className="panel-body" style={{paddingTop:8}}>{children}</div>
    </div>
  );
}

/* ── GRADIENT AVATAR ───────────────────────────────────────── */
export function GradientAvatar({ name, size = 34, fontSize = 11 }) {
  const initials = (name || '?').split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);
  return (
    <div style={{
      width: size, height: size,
      borderRadius: Math.round(size * 0.27),
      background: 'linear-gradient(135deg, var(--or-start), var(--or-end))',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize, fontWeight: 700, color: '#fff', flexShrink: 0,
    }}>{initials}</div>
  );
}

/* ── SCORE BAR ──────────────────────────────────────────────── */
export function ScoreBar({ score, max = 100 }) {
  const pct = (score / max) * 100;
  const color = pct >= 85 ? '#4ade80' : pct >= 70 ? 'var(--gd)' : pct >= 50 ? 'var(--or)' : 'var(--mu)';
  return (
    <div style={{display:'flex', alignItems:'center', gap:10, flex:1}}>
      <div style={{flex:1, height:3, background:'rgba(255,255,255,0.06)', borderRadius:2, overflow:'hidden'}}>
        <div style={{width:`${pct}%`, height:'100%', background: color, borderRadius:2, transition:'width 0.5s'}}/>
      </div>
      <span style={{fontFamily:'var(--mono)', fontSize:10, color, minWidth:24, textAlign:'right'}}>{score}</span>
    </div>
  );
}

/* ── BRANDED EMPTY STATE ────────────────────────────────────── */
export function EmptyState({ icon = '◌', title = 'Nothing here yet', message = 'Data will appear once activity begins.' }) {
  return (
    <div style={{textAlign:'center', padding:'40px 20px', fontFamily:'var(--mono)'}}>
      <div style={{
        width: 56, height: 56, borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(255,112,9,0.12), rgba(255,6,99,0.06))',
        border: '1px solid rgba(255,92,26,0.2)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 22, marginBottom: 14
      }}>{icon}</div>
      <div style={{fontSize:13, fontWeight:600, color:'#ddd', marginBottom:5, fontFamily:'var(--sans)'}}>{title}</div>
      <div style={{fontSize:11, color:'#888'}}>{message}</div>
    </div>
  );
}

/* ── TIER BADGE ─────────────────────────────────────────────── */
export function TierBadge({ tier }) {
  const map = { Executive: 'gd', Pro: 'or', Free: 'mu', Active: 'gr', Pending: 'gd', Closed: 'mu' };
  const cls = map[tier] || 'mu';
  return <span className={`badge ${cls}`}>{tier}</span>;
}

/* ── ICON CHIP (gradient bg square with icon) ───────────────── */
export function IconChip({ icon, size = 36, accent = 'orange' }) {
  const bg = accent === 'gold'
    ? 'linear-gradient(135deg, rgba(201,168,76,0.15), rgba(201,168,76,0.05))'
    : 'linear-gradient(135deg, rgba(255,112,9,0.18), rgba(255,6,99,0.10))';
  return (
    <div style={{
      width: size, height: size,
      borderRadius: Math.round(size * 0.25),
      background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.5),
      flexShrink: 0,
    }}>{icon}</div>
  );
}

/* ── ROW ITEM (consistent list row across modules) ──────────── */
export function RowItem({ left, title, subtitle, right, onClick }) {
  return (
    <div onClick={onClick} style={{
      display:'flex', alignItems:'center', gap:12,
      padding:'12px 0', borderBottom:'1px solid var(--b)',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'background 0.15s',
    }}
    onMouseEnter={e=>onClick && (e.currentTarget.style.background='rgba(255,255,255,0.02)')}
    onMouseLeave={e=>onClick && (e.currentTarget.style.background='transparent')}>
      {left}
      <div style={{flex:1, minWidth:0}}>
        <div style={{fontSize:13, fontWeight:600, color:'#fff', marginBottom:2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{title}</div>
        {subtitle && <div style={{fontSize:11, color:'#999', fontFamily:'var(--mono)'}}>{subtitle}</div>}
      </div>
      {right}
    </div>
  );
}
